/**
 * @fileoverview Dependencies Chart
 * @description Dependency call count and duration by type over time.
 */

import React, { useMemo } from 'react';
import { Typography, Paper, Skeleton, Box } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';

const DEP_COLORS = ['#0097A9', '#00838f', '#4dd0e1', '#80deea', '#b2ebf2', '#006064'];

const DependenciesChart = ({ data, loading }) => {
  const rows = data?.rows || [];

  const { timestamps, series } = useMemo(() => {
    if (!rows.length) return { timestamps: [], series: [] };

    const byTime = {};
    const typeSet = new Set();
    rows.forEach((r) => {
      const ts = r.timestamp;
      if (!byTime[ts]) byTime[ts] = {};
      const type = r.type || 'Unknown';
      typeSet.add(type);
      byTime[ts][type] = (byTime[ts][type] || 0) + (r.count || 0);
    });

    const sortedTimestamps = Object.keys(byTime).sort((a, b) => a.localeCompare(b));
    const types = [...typeSet];

    const chartSeries = types.map((type, i) => ({
      data: sortedTimestamps.map((ts) => byTime[ts][type] || 0),
      label: type.length > 25 ? type.substring(0, 25) + '…' : type,
      color: DEP_COLORS[i % DEP_COLORS.length],
      stack: 'deps',
    }));

    return {
      timestamps: sortedTimestamps.map((ts) => {
        const d = new Date(ts);
        return isNaN(d.getTime()) ? ts : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      }),
      series: chartSeries,
    };
  }, [rows]);

  if (loading) {
    return <Skeleton variant="rounded" height={300} sx={{ mb: 3 }} />;
  }

  if (!timestamps.length) {
    return (
      <Paper sx={{ p: 3, mb: 3, textAlign: 'center' }}>
        <Typography color="text.secondary">No dependency data available.</Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 2, mb: 3 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: '#0097A9' }}>
        Dependencies by Type
      </Typography>
      <Box sx={{ width: '100%', height: 300 }}>
        <BarChart
          height={270}
          xAxis={[{ data: timestamps, scaleType: 'band' }]}
          series={series}
          slotProps={{ legend: { direction: 'row', position: { vertical: 'top', horizontal: 'middle' } } }}
        />
      </Box>
    </Paper>
  );
};

export default DependenciesChart;
